import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { Link, useNavigate } from 'react-router-dom';
import '../styles.css';

function SignupMember({ recaptchaLoaded: recaptchaLoadedProp }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(!!recaptchaLoadedProp);
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);

  // Load script if not loaded, else use prop (for parent-managed)
  useEffect(() => {
    if (recaptchaLoadedProp) {
      setRecaptchaLoaded(true);
      return;
    }
    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => setRecaptchaLoaded(true);
      document.body.appendChild(script);
    } else {
      setRecaptchaLoaded(true);
    }
  }, [recaptchaLoadedProp]);

  // Dynamically render reCAPTCHA
  useEffect(() => {
    const renderRecaptcha = () => {
      if (!recaptchaLoaded || !window.grecaptcha || !recaptchaRef.current) return;
      // Only render once per mount
      if (recaptchaRef.current.childElementCount > 0) return;

      const attemptRender = () => {
        if (window.grecaptcha && window.grecaptcha.render) {
          try {
            window.grecaptcha.render(recaptchaRef.current, {
              sitekey: '6LczEuYqAAAAANYh6VG8jSj1Fmt6LKMK7Ee1OcfU', // Your actual sitekey here
              callback: (token) => { /* can handle if needed */ },
            });
          } catch (err) {
            setError('Failed to load reCAPTCHA. Please refresh the page or try again later.');
          }
        } else {
          setTimeout(attemptRender, 100);
        }
      };
      attemptRender();
    };

    renderRecaptcha();
  }, [recaptchaLoaded]);

  const verifyRecaptcha = async (token) => {
    try {
      const res = await fetch('/.netlify/functions/verify-recaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      return !!data.success;
    } catch {
      return false;
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);

    const honeypot = e.target.elements.honeypot.value;
    if (honeypot) {
      setError('Spam detected');
      setLoading(false);
      return;
    }

    if (!phoneNumber.trim()) {
      setError('Phone number is required.');
      setLoading(false);
      return;
    }

    if (!recaptchaLoaded || !window.grecaptcha) {
      setError('reCAPTCHA is not loaded yet. Please wait or refresh.');
      setLoading(false);
      return;
    }
    const token = window.grecaptcha.getResponse();
    if (!token) {
      setError('Please complete the reCAPTCHA.');
      setLoading(false);
      return;
    }
    const isRecaptchaValid = await verifyRecaptcha(token);
    if (!isRecaptchaValid) {
      setError('reCAPTCHA verification failed. Try again.');
      setLoading(false);
      window.grecaptcha.reset();
      return;
    }

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({ email, password });
      if (signUpError) throw signUpError;

      if (data.user) {
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: data.user.id,
            email: data.user.email,
            full_name: fullName,
            phone_number: phoneNumber,
            subscription_tier: null,
            is_admin: false,
          }, { onConflict: 'id' });

        if (profileError) throw profileError;

        setSuccess(true);

        setTimeout(() => {
          navigate('/profile');
        }, 1200);
      }
    } catch (err) {
      setError(err.message || 'Signup error');
    } finally {
      setLoading(false);
      if (window.grecaptcha) window.grecaptcha.reset();
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card max-w-lg mx-auto">
        <h2 className="auth-title text-green-900 text-2xl font-extrabold mb-2">Sign Up – Choir Member</h2>
        <p className="auth-subtitle text-green-800 mb-5">
          Create your free Choir Member account and join the choir community!
        </p>
        {success ? (
          <div className="text-green-700 my-4 text-center text-lg">
            Signup successful! Redirecting...
          </div>
        ) : (
          <form onSubmit={handleSignup} className="auth-form">
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                className="auth-input"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={loading}
                autoComplete="name"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                className="auth-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                className="auth-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label>
                Phone Number <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                className="auth-input"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
                disabled={loading}
                autoComplete="tel"
              />
            </div>
            <input type="text" name="honeypot" className="honeypot" style={{ display: 'none' }} />
            <div ref={recaptchaRef} className="g-recaptcha"></div>
            {!recaptchaLoaded && <p className="text-xs text-green-700 my-2">Loading reCAPTCHA...</p>}
            <button type="submit" className="auth-button w-full mt-4" disabled={loading || !recaptchaLoaded}>
              {loading ? 'Signing Up...' : 'Sign Up'}
            </button>
          </form>
        )}
        {error && <div className="text-red-700 mt-3">{error}</div>}
        <div className="mt-6 text-center text-green-700">
          Already have an account?{' '}
          <Link to="/login" className="text-green-800 underline">
            Log in here
          </Link>
        </div>
      </div>
    </div>
  );
}

export default SignupMember;
